import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserCheck, BarChart2, BadgeCheck, Info } from "lucide-react";
import { useWizard } from "@/contexts/WizardContext";
import { useToast } from "@/hooks/use-toast";
import { LockedBanner } from "../common/LockedBanner";
import { CardPanelHeader } from "../common/CardPanelHeader";
import { InfoCallout } from "../common/InfoCallout";
import { SectionCard } from "../common/SectionCard";
import { ProgressCard } from "../common/ProgressCard";
import { cn } from "@/lib/utils";
import { CurrencySelect } from "@/components/workflow/common/CurrencySelect";

interface AccountabilityCardProps {
  onCollapse: () => void;
}

function ChoiceCard({
  checked,
  disabled,
  label,
  name,
  id,
  onChange,
}: {
  checked: boolean;
  disabled: boolean;
  label: string;
  name: string;
  id: string;
  onChange: () => void;
}) {
  return (
    <label
      htmlFor={id}
      className={cn(
        "flex min-h-[72px] flex-1 cursor-pointer items-center gap-3 rounded-2xl border p-4 transition-colors",
        checked ? "border-primary/20 bg-primary/5" : "border-slate-200 bg-white hover:border-slate-300",
        disabled && "cursor-not-allowed opacity-60",
      )}
    >
      <input
        id={id}
        type="radio"
        name={name}
        className="sr-only"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
      <span
        aria-hidden="true"
        className={cn(
          "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border bg-white",
          checked ? "border-primary" : "border-slate-500",
        )}
      >
        <span className={cn("h-2 w-2 rounded-full bg-primary", !checked && "opacity-0")} />
      </span>
      <span className="text-sm font-medium leading-5 text-slate-900">{label}</span>
    </label>
  );
}

const SECTORS: { key: string; label: string }[] = [
  { key: "controversialWeapons", label: "Revenue from Controversial Weapons sector" },
  { key: "tobacco",              label: "Revenue from Tobacco sector" },
  { key: "fossilFuels",          label: "Revenue from Fossil Fuels sector" },
  { key: "pesticides",           label: "Revenue from Pesticides sector" },
];

export function AccountabilityCard({ onCollapse }: AccountabilityCardProps) {
  const { accountabilityData, updateAccountabilityData, accountabilityCompletion, forceSave, saveStatus, isLocked, isReadOnly } = useWizard();
  const disabled = isLocked || isReadOnly;
  const { toast } = useToast();

  const handleSave = async () => {
    try {
      await forceSave();
      toast({
        title: "Saved successfully",
        description: "Accountability data has been saved.",
      });
    } catch (error) {
      toast({
        title: "Save failed",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleControversialSectorChange = (sector: string, amount: string, currency: string) => {
    const current = accountabilityData.controversialSectorsRevenue || {};
    updateAccountabilityData({
      controversialSectorsRevenue: {
        ...current,
        [sector]: { amount, currency }
      }
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <CardPanelHeader
        title="Accountability (C8 + C2)"
        description="Track sustainability ownership, controversial sectors, and benchmark status"
        onClose={onCollapse}
        onSave={() => { void handleSave(); }}
        saveStatus={saveStatus}
        disabled={disabled}
        tag="Governance"
      />

      {disabled && <LockedBanner />}

      <div className="flex flex-col gap-6">
        {/* Accountability Owner */}
        <SectionCard
          header={
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-center gap-2">
                <span className="shrink-0 text-primary">
                  <UserCheck className="h-5 w-5" />
                </span>
                <h3 className="text-xl font-semibold leading-7 text-slate-900">
                  Accountability Owner (Optional)
                </h3>
              </div>
            </div>
          }
        >
          <div className="flex flex-col gap-2">
            <Label className="form-label">Role/Title</Label>
            <Input
              className="form-input h-12"
              placeholder="e.g. Chief Executive Officer, Head of Sustainability, Managing Director, etc."
              value={accountabilityData.sustainabilityOwnerRole || ""}
              onChange={(e) => updateAccountabilityData({ sustainabilityOwnerRole: e.target.value })}
              disabled={disabled}
            />
            <p className="flex items-center gap-1.5 pt-1 text-xs leading-4 text-slate-400">
              <Info className="h-3 w-3 shrink-0" />
              Enter the job title of the person responsible for sustainability
            </p>
          </div>
        </SectionCard>

        {/* Sector Revenue Disclosure */}
        <SectionCard
          header={
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-center gap-2">
                <span className="shrink-0 text-primary">
                  <BarChart2 className="h-5 w-5" />
                </span>
                <h3 className="text-xl font-semibold leading-7 text-slate-900">
                  Sector Revenue Disclosure (Only if Applicable)
                </h3>
              </div>
            </div>
          }
        >
          <div className="flex flex-col gap-4">
            {SECTORS.map(({ key, label }) => {
              const entry = accountabilityData.controversialSectorsRevenue?.[key as keyof NonNullable<typeof accountabilityData.controversialSectorsRevenue>];
              return (
                <div key={key} className="flex flex-col gap-2">
                  <span className="form-label">{label}</span>
                  <div className="flex items-center overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
                    <CurrencySelect
                      value={entry?.currency || "usd"}
                      onValueChange={(value) => handleControversialSectorChange(key, entry?.amount || "", value)}
                      disabled={disabled}
                      lowercase
                      triggerClassName="w-20 shrink-0 rounded-none border-0 bg-transparent text-xs font-bold text-slate-500 shadow-none focus:ring-0"
                    />
                    <div className="h-6 w-px shrink-0 bg-slate-200" />
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="Amount"
                      value={entry?.amount || ""}
                      onChange={(e) => handleControversialSectorChange(key, e.target.value, entry?.currency || "usd")}
                      disabled={disabled}
                      className="h-10 flex-1 rounded-none border-0 bg-transparent text-sm shadow-none focus-visible:ring-0"
                    />
                  </div>
                </div>
              );
            })}

            <InfoCallout
              label="Tip:"
              text="Use management accounts or segment revenue reporting as evidence"
            />
          </div>
        </SectionCard>

        {/* Benchmark Exclusion */}
        <SectionCard
          header={
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-center gap-2">
                <span className="shrink-0 text-primary">
                  <BadgeCheck className="h-5 w-5" />
                </span>
                <h3 className="text-xl font-semibold leading-7 text-slate-900">
                  Benchmark Exclusion
                </h3>
              </div>
            </div>
          }
        >
          <div className="flex flex-col gap-4">
            <p className="text-base font-medium leading-6 text-slate-900">
              Are you excluded from EU Paris-aligned reference benchmarks?{" "}
              <span className="font-bold text-red-700">*</span>
            </p>
            <div className="grid gap-4 md:grid-cols-2">
              <ChoiceCard
                checked={accountabilityData.benchmarkExclusion === true}
                disabled={disabled}
                label="Yes"
                name="benchmark_exclusion"
                id="bench_yes"
                onChange={() => updateAccountabilityData({ benchmarkExclusion: true })}
              />
              <ChoiceCard
                checked={accountabilityData.benchmarkExclusion === false}
                disabled={disabled}
                label="No"
                name="benchmark_exclusion"
                id="bench_no"
                onChange={() => updateAccountabilityData({ benchmarkExclusion: false })}
              />
            </div>
            <InfoCallout
              label="Note:"
              text="Paris-aligned benchmarks exclude companies with certain ESG violations or controversial practices"
            />
          </div>
        </SectionCard>

        <ProgressCard completion={accountabilityCompletion} />
      </div>
    </div>
  );
}
